import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  FormGroup,
  Label,
  Input,
  Badge,
  Spinner,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCheck,
  FaCoins,
  FaFileInvoice,
  FaMoneyBillWave,
  FaSearch,
  FaTimes,
  FaUser,
} from "react-icons/fa";

import { reglementActions } from "../../sagas/reglementSlice";
import { clientActions } from "../../sagas/clientSlice";

import { preventScroll } from "../../helpers/utility";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CBL_PAGE,
  CPMT_PRODUCTS_PAGE,
  CPMT_RECEPTION_PAGE,
  CPMT_STOCK_PAGE,
  CPMT_TRANSFER_PAGE,
  PAY_PAGE,
  RETOUR_PAGE,
} from "../../routes/routeConstants";

import POSHeader from "./POSHeader";
import POSKeypadModal from "./POSKeypadModal";

import "./ReglementForm.css";
import POSNumericKeypadModal from "./POSNumericKeypadModal";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import DocumentSelectionTablet from "./DocumentSelectionModal";

const ReglementForm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const reglement = location.state?.reglement || null;
  const canEdit = (reglement && reglement.statut === "EN_ATTENTE") || !reglement;

  const { clients } = useSelector((state) => state.clients || {});
  const { businessName, email, address, telephone, fullName, commercialId, pointOfSaleId } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("AVANCE");

  // POS UI States
  const [clientModal, setClientModal] = useState(false);
  const [clientQuery, setClientQuery] = useState("");

  const [showClientKeypad, setShowClientKeypad] = useState(false);
  const [showMontantKeypad, setShowMontantKeypad] = useState(false);
  const { loading } = useSelector(state => state.reglements);
  const [selectedClient, setSelectedClient] = useState(null);
  const [typeReglement, setTypeReglement] = useState("BON_LIVRAISON");
  const { list: bonLivraisonsNonRegles } = useSelector((state) => state.deliveryNotes);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const { currentShift, loading: shiftLoading, error: shiftError } = useSelector(
    (state) => state.shift
  );

  // Filters used by advance list refresh
  const [filters, setFilters] = useState({
    clientId: null,
    dateFrom: null,
    dateTo: null,
    statut: "EN_ATTENTE",
  });

  // Fetch clients
  useEffect(() => {
    dispatch(clientActions.search({ query: "" }));
  }, [dispatch]);

  useEffect(() => {
    if (reglement && clients?.length) {
      const client = clients.find((c) => c.id === reglement.clientId);
      if (client) {
        setSelectedClient({
          id: client.id,
          label:
            client.typeClient === "PARTICULIER"
              ? `${client.nom} ${client.prenom || ""}`
              : client.entreprise,
          solde: client.solde,
          totalDebit: client.totalDebit,
          totalCredit: client.totalCredit,
        });

        setFilters((f) => ({ ...f, clientId: client.id }));
      }
      setTypeReglement(reglement.typeReglement);
    }
  }, [reglement, clients]);

  // Load documents when client changes
  useEffect(() => {
    if (selectedClient) {
      let payload = {
        clientId: selectedClient.id,
      };
      dispatch(deliveryActions.listImpayesComptoir(payload));
    }
  }, [selectedClient, dispatch]);

  useEffect(() => {
    if (!reglement || !selectedClient || !bonLivraisonsNonRegles?.length) {
      return;
    }
    console.log(reglement)
    const numeros = (reglement.numBonLivraisons || '')
      .split(',')
      .map(n => n.trim())
      .filter(Boolean); // removes empty strings

    if (numeros.length === 0) {
      setSelectedDocuments([]);
      return;
    }

    const initialDocs = bonLivraisonsNonRegles.filter(d =>
      numeros.includes(String(d.numero))
    );

    setSelectedDocuments(initialDocs);
  }, [reglement, selectedClient, bonLivraisonsNonRegles]);


  // Validation schema
  const validationSchema = Yup.object().shape({
    clientId: Yup.number().required(t("reglement.clientRequired")),
    dateReglement: Yup.date().required(t("reglement.dateReglementRequired")),
    modeReglement: Yup.string().required(t("reglement.modeReglementRequired")),
    statut: Yup.string().required(t("reglement.statutRequired")),
  });

  // Initial values
  const initialValues = {
    id: reglement?.id || null,
    reference: reglement?.reference || "",
    clientId: reglement?.clientId || "",
    shiftId: currentShift.id,
    typeReglement: reglement?.typeReglement || "BON_LIVRAISON",
    montantRegle: reglement?.montantRegle || 0,
    dateReglement:
      reglement?.dateReglement ||
      new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Casablanca" }),
    statut: reglement?.statut || "EN_ATTENTE",
    commentaire: reglement?.commentaire || "",
    modeReglement: reglement?.modeReglement || "ESPECES"
  };

  const modeReglementOptions = useMemo(
    () => [
      { value: "ESPECES", label: t("reglement.especes") },
      { value: "CHEQUE", label: t("reglement.cheque") },
      { value: "VIREMENT", label: t("reglement.virement") },
      { value: "CARTE_BANCAIRE", label: t("reglement.carteBancaire") },
    ],
    [t]
  );

  const clientOptions = useMemo(() => {
    return (
      clients?.map((client) => ({
        id: client.id,
        label:
          client.typeClient === "PARTICULIER"
            ? `${client.nom} ${client.prenom || ""}`
            : client.entreprise,
        solde: client.solde,
        totalDebit: client.totalDebit,
        totalCredit: client.totalCredit,
        raw: client,
      })) ?? []
    );
  }, [clients]);

  const filteredClients = useMemo(() => {
    const q = (clientQuery || "").toLowerCase().trim();
    if (!q) return clientOptions;
    return clientOptions.filter((c) => c.label.toLowerCase().includes(q));
  }, [clientOptions, clientQuery]);

  const handleSubmit = (values, { setSubmitting }) => {
    const payload = {
      data: {
        ...values,
        numBonLivraisons: selectedDocuments
                    .filter(doc => doc.numero?.toString().startsWith("BL") || doc.numero?.toString().startsWith("CBL"))
                    .map(doc => doc.numero)
                    .join(','),
      },
      onSuccess: () => {
        Swal.fire({
          icon: "success",
          title: t("common.success"),
          text: reglement
            ? t("reglement.updateSuccess")
            : t("reglement.avanceCreateSuccess"),
        });
        dispatch(reglementActions.avanceClient(filters));
        navigate(PAY_PAGE);
      },
      onError: (error) => {
        Swal.fire({
          icon: "error",
          title: t("common.error"),
          text: error.message || t("common.errorOccurred"),
        });
        setSubmitting(false);
      },
    };

    if (reglement) {
      dispatch(reglementActions.updateRequest({ ...payload, id: reglement.id }));
    } else {
      dispatch(reglementActions.createRequest(payload));
    }
  };

  const applyClient = useCallback((c, setFieldValue) => {
    setSelectedClient(c);
    setFieldValue("clientId", c?.id || "");
    setFilters((f) => ({ ...f, clientId: c?.id || null }));
    setClientModal(false);
  }, []);

  const handleDocumentsSelected = (documents) => {
    setSelectedDocuments(documents);
  };

  return (
    <Container fluid className="pos-page">
      <POSHeader
        businessName={businessName || "MATJARPOS"}
        commercialId={commercialId}
        pointOfSaleId={pointOfSaleId}
        fullName={fullName}
        activeTab={activeTab}
        onChangeTab={(tab) => {
          setActiveTab(tab);
          if (tab === "VENTES") navigate(CBL_PAGE);
          if (tab === "STOCK") navigate(CPMT_STOCK_PAGE);
          if (tab === "PRODUCTS") navigate(CPMT_PRODUCTS_PAGE);
          if (tab === "AVANCE") navigate(PAY_PAGE);
          if (tab === "RETOURS") navigate(RETOUR_PAGE);
          if (tab === "RECEPTION") navigate(CPMT_RECEPTION_PAGE);
          if (tab === "TRANSFER") navigate(CPMT_TRANSFER_PAGE);
        }}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur }) => (
          <Form>
            {/* ======= POS TOP TOOLBAR ======= */}
            <Card className="pos-actionbar">
              <CardBody className="pos-actionbar-body">
                <div className="pos-actions-left">
                  <Button
                    type="button"
                    color="light"
                    className="pos-action-btn"
                    onClick={() => navigate(PAY_PAGE)}
                  >
                    <FaArrowLeft className="me-2" /> Retour
                  </Button>
                </div>

                <div className="pos-actions-right">
                  <Button
                    type="submit"
                    color="primary"
                    className="pos-action-btn-primary"
                    disabled={isSubmitting || !selectedClient || !canEdit}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <FaCheck className="me-2" /> {reglement ? t("common.update") : t("common.create")}
                      </>
                    )}
                  </Button>
                </div>
              </CardBody>
            </Card>
            {/* ======= MAIN FORM ======= */}
            <Row className="g-3">
              {/* CLIENT + DATE */}
              <Col md={12}>
                <Card className="pos-card">
                  <CardBody className="pos-card-body">
                    <Row className="g-3">
                      {/* CLIENT (POS MODAL) */}
                      <Col md={3}>
                        <FormGroup className="mb-0">
                          <Label className="pos-label">
                            <FaUser className="me-2" />
                            {t("reglement.client")} *
                          </Label>

                          <div className="pos-input-group">
                            <Input
                              value={selectedClient?.label || ""}
                              readOnly
                              placeholder={t("reglement.selectClient")}
                              className={`pos-input ${errors.clientId && touched.clientId ? "is-invalid" : ""}`}
                              onClick={() => {
                                if (!canEdit) return;
                                setClientModal(true);
                              }}
                            />
                            <Button
                              type="button"
                              color="primary"
                              className="pos-side-btn"
                              disabled={!canEdit}
                              onClick={() => setClientModal(true)}
                            >
                              <FaSearch />
                            </Button>
                          </div>

                          <ErrorMessage name="clientId" component="div" className="pos-error" />
                        </FormGroup>
                      </Col>

                      {/* DATE */}
                      <Col md={3}>
                        <FormGroup className="mb-0">
                          <Label className="pos-label">
                            <FaCalendarAlt className="me-2" />
                            {t("reglement.dateReglement")} *
                          </Label>
                          <Field
                            name="dateReglement"
                            type="date"
                            className={`form-control pos-input ${errors.dateReglement && touched.dateReglement ? "is-invalid" : ""
                              }`}
                            disabled={!canEdit}
                          />
                          <ErrorMessage name="dateReglement" component="div" className="pos-error" />
                        </FormGroup>
                      </Col>
                      {
                        selectedDocuments && selectedDocuments.length > 0 &&
                        <>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label className="pos-label">
                                <FaCoins className="me-2" />
                                {t("reglement.montantRegle")} *
                              </Label>

                              <div className="pos-input-group">
                                <Input
                                  value={values.montantRegle}
                                  readOnly
                                  inputMode="none"
                                  className={`pos-input ${errors.montantRegle && touched.montantRegle ? "is-invalid" : ""}`}
                                  placeholder="0.00"
                                  onClick={() => {
                                    if (!canEdit) return;
                                    setShowMontantKeypad(true);
                                  }}
                                  onFocus={(e) => {
                                    e.target.select();
                                    e.target.addEventListener("wheel", preventScroll, { passive: false });
                                  }}
                                  onBlur={(e) => {
                                    e.target.removeEventListener("wheel", preventScroll);
                                  }}
                                  disabled={!canEdit}
                                />
                                <Button
                                  type="button"
                                  color="primary"
                                  className="pos-side-btn"
                                  disabled={!canEdit}
                                  onClick={() => setShowMontantKeypad(true)}
                                >
                                  123
                                </Button>
                              </div>

                              <ErrorMessage name="montantRegle" component="div" className="pos-error" />
                            </FormGroup>
                          </Col>

                          {/* MODE REGLEMENT */}
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label className="pos-label">
                                <FaMoneyBillWave className="me-2" />
                                {t("reglement.modeReglement")} *
                              </Label>
                              <Field
                                as="select"
                                name="modeReglement"
                                className={`form-control pos-input ${errors.modeReglement && touched.modeReglement ? "is-invalid" : ""
                                  }`}
                                disabled={!canEdit}
                              >
                                <option value="">{t("reglement.selectModeReglement")}</option>
                                {modeReglementOptions.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </Field>
                              <ErrorMessage name="modeReglement" component="div" className="pos-error" />
                            </FormGroup>
                          </Col>
                        </>
                      }

                    </Row>
                  </CardBody>
                </Card>
              </Col>
              {selectedClient && canEdit &&
                <Col md={12}>
                  <DocumentSelectionTablet
                    typeDocument={typeReglement}
                    documents={bonLivraisonsNonRegles}
                    loading={loading}
                    selectedDocuments={selectedDocuments}
                    onDocumentsSelected={handleDocumentsSelected} />
                </Col>
              }




            </Row>



            {/* ============================
                CLIENT MODAL POS (NO SELECT)
            ============================ */}
            <Modal isOpen={clientModal} toggle={() => setClientModal(false)} centered size="lg">
              <ModalHeader toggle={() => setClientModal(false)}>
                Choisir un client
              </ModalHeader>
              <ModalBody>
                <div className="pos-client-modal-toolbar">
                  <Button
                    color="primary"
                    className="pos-client-searchbtn"
                    onClick={() => setShowClientKeypad(true)}
                  >
                    <FaSearch className="me-2" />
                    Rechercher
                  </Button>

                  <Input
                    value={clientQuery}
                    readOnly
                    className="pos-client-query"
                    placeholder="Recherche client..."
                  />

                  {clientQuery ? (
                    <Button color="light" onClick={() => setClientQuery("")}>
                      <FaTimes />
                    </Button>
                  ) : null}
                </div>

                <div className="pos-client-grid">
                  {filteredClients.map((c) => (
                    <button
                      key={c.id}
                      className={`pos-client-item ${selectedClient?.id === c.id ? "active" : ""}`}
                      type="button"
                      onClick={() => applyClient(c, setFieldValue)}
                    >
                      <div className="pos-client-name">{c.label}</div>
                      <div className="pos-client-meta">
                        <span>Débit: {Number(c.totalDebit || 0).toFixed(2)}</span>
                        <span>Crédit: {Number(c.totalCredit || 0).toFixed(2)}</span>
                        <span className="pos-client-solde">
                          Solde: {Number(c.solde || 0).toFixed(2)}
                        </span>
                      </div>
                    </button>
                  ))}

                  {filteredClients.length === 0 && (
                    <div className="text-center text-muted py-4">
                      Aucun client trouvé
                    </div>
                  )}
                </div>
              </ModalBody>
            </Modal>

            {/* ===== CLIENT SEARCH KEYPAD ===== */}
            <POSKeypadModal
              isOpen={showClientKeypad}
              toggle={() => setShowClientKeypad(false)}
              value={clientQuery}
              onChange={(val) => setClientQuery((val || "").toUpperCase())}
              onSearch={() => setShowClientKeypad(false)}
              title="Recherche Client"
            />

            <POSNumericKeypadModal
              isOpen={showMontantKeypad}
              toggle={() => setShowMontantKeypad(false)}
              title="Montant"
              value={values.montantRegle}
              allowDecimal={true}
              onConfirm={(val) => {
                const amount = Number(val || 0);
                if (Number.isNaN(amount)) return;
                setFieldValue("montantRegle", amount);
              }}
            />
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default ReglementForm;
